import type { AssistantCard, InboxItem, StageView as BaseView } from '@secretario/shared';

/** Visão do palco no aparelho: a do contrato, mais o conteúdo do cartão interativo. */
export type StageView = BaseView & {
  card?: AssistantCard;
  /** O que a pessoa pediu, nas palavras dela: a origem que o painel mostra. */
  asked?: string;
  /** Nome do item citado: a linha dele pulsa uma vez. */
  focus?: string;
  /** Pergunta de esclarecimento: a pergunta e as opções para tocar. */
  ask?: { question: string; options: string[] };
  /** Mensagem que chegou por outro app: quem escreveu, o texto e a resposta pelo aviso. */
  inbox?: {
    item: InboxItem;
    draft?: string;
    /** As falas da conversa que chegaram em sequência (o cartão mostra todas). */
    lines?: { text: string; at: number }[];
    more?: number;
    onNext?: () => void;
  };
};

/**
 * Estado do palco: no máximo um painel e três cartões de cada vez, com
 * histórico curto para "mostra de novo". Função pura, sem relógio próprio:
 * quem chama informa a hora, o que deixa as regras testáveis.
 */
export type StageMode = 'voice' | 'silent';

export type StageState = {
  panel: StageView | null;
  cards: StageView[];
  /** Últimas visões fechadas, da mais recente para a mais antiga. */
  history: StageView[];
  mode: StageMode;
  /** Quando o Secretário mexeu na tela pela última vez (ms). */
  assistantChangedAt: number;
};

export type StageAction =
  | {
      type: 'show';
      view: StageView;
      now: number;
      typing?: boolean;
      /** A fila do ao vivo já deu o ritmo: entra sem esperar o 1,5 s. */
      force?: boolean;
    }
  | { type: 'close'; target: 'top' | 'all' | string }
  | { type: 'reshow' }
  /** Completa a origem do painel aberto pelo Secretário com o pedido entendido. */
  | { type: 'asked'; asked: string }
  | { type: 'mode'; mode: StageMode };

export const MAX_CARDS = 3;
export const HISTORY_SIZE = 10;
/** O Secretário troca a tela no máximo uma vez a cada 1,5 s; o toque da pessoa sempre vale. */
export const ASSISTANT_PACE_MS = 1500;

export const INITIAL_STAGE: StageState = {
  panel: null,
  cards: [],
  history: [],
  mode: 'voice',
  assistantChangedAt: 0,
};

function remember(history: StageView[], view: StageView | null): StageView[] {
  if (!view || view.kind === 'notice') return history;
  return [view, ...history.filter((item) => item.id !== view.id)].slice(0, HISTORY_SIZE);
}

/** Mesmo tipo e mesmo destino: a busca refeita troca a sugestão velha, não empilha. */
function familyOf(view: StageView): string | null {
  return view.card?.href ? `${view.card.kind}|${view.card.href}` : null;
}

function withCard(cards: StageView[], view: StageView): StageView[] {
  const family = familyOf(view);
  const kept = cards.filter(
    (item) => item.id !== view.id && (family === null || familyOf(item) !== family),
  );
  // Passou do limite: sai primeiro o aviso mais antigo; a mensagem de outra pessoa,
  // que espera uma decisão, só sai se não houver outro jeito.
  const next = [...kept, view];
  while (next.length > MAX_CARDS) {
    const oldest = next.findIndex((item) => !item.inbox);
    next.splice(oldest >= 0 ? oldest : 0, 1);
  }
  return next;
}

export function stageReducer(state: StageState, action: StageAction): StageState {
  switch (action.type) {
    case 'show': {
      const fromAssistant = action.view.source === 'assistant';
      const asCard = action.view.level === 'card';
      if (fromAssistant) {
        // Tela "nervosa" não: o Secretário não troca um painel por outro em menos de
        // 1,5 s. Cartão é outra camada: duas consultas no mesmo pedido (a agenda e a
        // previsão) mostram o painel e o cartão, sem um apagar o outro.
        if (!asCard && !action.force && action.now - state.assistantChangedAt < ASSISTANT_PACE_MS)
          return state;
        // Nada abre por cima de quem está digitando: o painel vira cartão.
        if (action.typing && !asCard) {
          return {
            ...state,
            cards: withCard(state.cards, { ...action.view, level: 'card' }),
            assistantChangedAt: action.now,
          };
        }
      }
      const stamp = fromAssistant && !asCard ? action.now : state.assistantChangedAt;
      if (asCard) {
        return { ...state, cards: withCard(state.cards, action.view), assistantChangedAt: stamp };
      }
      if (state.panel?.id === action.view.id) {
        // O mesmo painel, pedido de novo: só atualiza a origem e o item citado.
        const same =
          state.panel.asked === action.view.asked && state.panel.focus === action.view.focus;
        return same ? state : { ...state, panel: action.view, assistantChangedAt: stamp };
      }
      return {
        ...state,
        panel: action.view,
        history: remember(state.history, state.panel),
        assistantChangedAt: stamp,
      };
    }
    case 'close': {
      if (action.target === 'all') {
        return { ...state, panel: null, cards: [], history: remember(state.history, state.panel) };
      }
      if (action.target === 'top') {
        // O que está por cima sai primeiro: cartão, depois painel.
        if (state.cards.length > 0) return { ...state, cards: state.cards.slice(0, -1) };
        if (state.panel) {
          return { ...state, panel: null, history: remember(state.history, state.panel) };
        }
        return state;
      }
      if (state.panel?.id === action.target) {
        return { ...state, panel: null, history: remember(state.history, state.panel) };
      }
      const cards = state.cards.filter((item) => item.id !== action.target);
      return cards.length === state.cards.length ? state : { ...state, cards };
    }
    case 'asked': {
      if (state.panel?.source !== 'assistant' || state.panel.asked === action.asked) return state;
      return { ...state, panel: { ...state.panel, asked: action.asked } };
    }
    case 'reshow': {
      const [last, ...rest] = state.history;
      if (!last) return state;
      return {
        ...state,
        panel: { ...last, source: 'history' },
        history: remember(rest, state.panel),
      };
    }
    case 'mode':
      return state.mode === action.mode ? state : { ...state, mode: action.mode };
  }
}

/** Como o personagem aparece: cheio no repouso, busto com painel aberto. */
export function presenceOf(state: StageState): 'full' | 'bust' {
  return state.panel ? 'bust' : 'full';
}
